import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  CheckCircle, AlertTriangle, Zap, Radio,
  MessageSquare, Hash, Bell, Shield
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { ProductPageNav, ProductPageFooter } from '@/components/ProductPageNav'
import { SEOHead } from '@/components/SEOHead'

// ── SVG: Platform Feed ──────────────────────────────────────────────────────

function PlatformFeedSVG() {
  const platforms = [
    { id: 'X',  label: 'Twitter/X',  color: '#3b82f6', y: 30,  rate: '2.1M posts/day' },
    { id: 'TG', label: 'Telegram',   color: '#06b6d4', y: 95,  rate: '800K msg/day' },
    { id: 'RD', label: 'Reddit',     color: '#f59e0b', y: 160, rate: '1.3M posts/day' },
    { id: 'DC', label: 'Discord',    color: '#8b5cf6', y: 225, rate: '600K msg/day' },
  ]
  const outputs = [
    { label: 'NLP Analysis', sub: 'entities · sentiment',  color: '#22c55e', y: 60  },
    { label: 'Alert Engine', sub: 'webhook · push',        color: '#ef4444', y: 140 },
    { label: 'Dashboard',    sub: 'real-time feed',        color: '#64748b', y: 220 },
  ]

  return (
    <svg viewBox="0 0 700 280" className="w-full" aria-label="Platform feed diagram">
      {/* Platform nodes */}
      {platforms.map((p) => (
        <g key={p.id}>
          <rect x="20" y={p.y} width="120" height="44" rx="8"
            fill="#0d1117" stroke={p.color} strokeWidth="1.5" />
          <text x="44" y={p.y + 22} fill={p.color} fontSize="13" fontWeight="800">{p.id}</text>
          <text x="62" y={p.y + 18} fill="#e2e8f0" fontSize="11" fontWeight="600">{p.label}</text>
          <text x="62" y={p.y + 32} fill="#64748b" fontSize="9">{p.rate}</text>
          {/* dashed connector to engine */}
          <line
            x1="140" y1={p.y + 22}
            x2="310" y2="140"
            stroke="#1e3a1e" strokeWidth="1.5" strokeDasharray="6 4"
          />
          {/* rate label midpoint */}
          <text
            x={(140 + 310) / 2 - 16}
            y={(p.y + 22 + 140) / 2 - 4}
            fill="#374151" fontSize="8"
          >{p.rate}</text>
        </g>
      ))}

      {/* Stream Engine oval */}
      <ellipse cx="370" cy="140" rx="60" ry="36"
        fill="#0d1117" stroke="#22c55e" strokeWidth="2" />
      <ellipse cx="370" cy="140" rx="50" ry="27" fill="#22c55e18" />
      <text x="370" y="136" textAnchor="middle" fill="#22c55e" fontSize="11" fontWeight="700">Stream</text>
      <text x="370" y="150" textAnchor="middle" fill="#22c55e" fontSize="11" fontWeight="700">Engine</text>

      {/* Output nodes */}
      {outputs.map((o) => (
        <g key={o.label}>
          <line
            x1="430" y1="140"
            x2="510" y2={o.y + 22}
            stroke="#1e3a1e" strokeWidth="1.5" strokeDasharray="6 4"
          />
          <rect x="510" y={o.y} width="162" height="44" rx="8"
            fill="#0d1117" stroke={o.color} strokeWidth="1.5" />
          <text x="591" y={o.y + 18} textAnchor="middle" fill={o.color} fontSize="11" fontWeight="700">{o.label}</text>
          <text x="591" y={o.y + 32} textAnchor="middle" fill="#64748b" fontSize="9">{o.sub}</text>
        </g>
      ))}
    </svg>
  )
}

// ── SVG: Keyword Monitor ─────────────────────────────────────────────────────

function KeywordMonitorSVG() {
  return (
    <svg viewBox="0 0 600 180" className="w-full" aria-label="Keyword query builder">
      {/* Terminal box */}
      <rect x="20" y="10" width="380" height="130" rx="10"
        fill="#0d1117" stroke="#1e3a1e" strokeWidth="1.5" />
      {/* Title bar */}
      <rect x="20" y="10" width="380" height="26" rx="10" fill="#111a11" />
      <rect x="20" y="28" width="380" height="8" fill="#111a11" />
      <circle cx="38" cy="23" r="5" fill="#ef4444" opacity="0.7" />
      <circle cx="54" cy="23" r="5" fill="#f59e0b" opacity="0.7" />
      <circle cx="70" cy="23" r="5" fill="#22c55e" opacity="0.7" />
      <text x="200" y="27" textAnchor="middle" fill="#64748b" fontSize="10">Query Builder</text>

      {/* Row 1: ransomware AND India */}
      <text x="38" y="60" fill="#64748b" fontSize="9">MATCH</text>
      <rect x="80" y="48" width="90" height="20" rx="5" fill="#22c55e20" stroke="#22c55e" strokeWidth="1" />
      <text x="125" y="62" textAnchor="middle" fill="#22c55e" fontSize="10" fontWeight="600">ransomware</text>
      <text x="180" y="62" fill="#94a3b8" fontSize="10" fontWeight="700">AND</text>
      <rect x="206" y="48" width="52" height="20" rx="5" fill="#22c55e20" stroke="#22c55e" strokeWidth="1" />
      <text x="232" y="62" textAnchor="middle" fill="#22c55e" fontSize="10" fontWeight="600">India</text>

      {/* Row 2: OR #DDoS */}
      <text x="38" y="96" fill="#64748b" fontSize="9">OR</text>
      <rect x="80" y="84" width="60" height="20" rx="5" fill="#f59e0b20" stroke="#f59e0b" strokeWidth="1" />
      <text x="110" y="98" textAnchor="middle" fill="#f59e0b" fontSize="10" fontWeight="600">#DDoS</text>

      {/* Row 3: NOT false positive */}
      <text x="38" y="130" fill="#64748b" fontSize="9">NOT</text>
      <rect x="80" y="118" width="98" height="20" rx="5" fill="#ef444420" stroke="#ef4444" strokeWidth="1" />
      <text x="129" y="132" textAnchor="middle" fill="#ef4444" fontSize="10" fontWeight="600">false positive</text>

      {/* Results panel */}
      <rect x="420" y="10" width="160" height="130" rx="10"
        fill="#0d1117" stroke="#1e3a1e" strokeWidth="1.5" />
      <text x="500" y="36" textAnchor="middle" fill="#22c55e" fontSize="13" fontWeight="800">1,247</text>
      <text x="500" y="52" textAnchor="middle" fill="#64748b" fontSize="9">matches · last 24h</text>

      {/* Sparkline */}
      <polyline
        points="436,120 450,115 464,118 478,100 492,90 506,78 520,82 534,70 548,76 562,65"
        fill="none" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      />
      {/* spike highlight */}
      <circle cx="506" cy="78" r="4" fill="#22c55e" />
      <text x="506" y="72" textAnchor="middle" fill="#22c55e" fontSize="8">spike</text>
      {/* baseline */}
      <line x1="436" y1="125" x2="568" y2="125" stroke="#1e3a1e" strokeWidth="1" />
    </svg>
  )
}

// ── SVG: NLP Pipeline ────────────────────────────────────────────────────────

function NLPPipelineSVG() {
  const stages = [
    { label: 'Raw Text',      sub: 'post / message',    color: '#64748b', x: 50  },
    { label: 'Tokenize',      sub: 'split & clean',     color: '#3b82f6', x: 180 },
    { label: 'NER',           sub: 'entity extraction', color: '#22c55e', x: 310 },
    { label: 'Sentiment',     sub: '−1.0 → +1.0',       color: '#f59e0b', x: 440 },
    { label: 'Output',        sub: 'structured JSON',   color: '#8b5cf6', x: 570 },
  ]
  return (
    <svg viewBox="0 0 660 120" className="w-full" aria-label="NLP pipeline">
      {/* Connecting line */}
      <line x1="50" y1="55" x2="620" y2="55"
        stroke="#1e3a1e" strokeWidth="1.5" strokeDasharray="6 4" />
      {stages.map((s, i) => (
        <g key={s.label}>
          {i > 0 && (
            <polygon
              points={`${s.x - 18},51 ${s.x - 6},55 ${s.x - 18},59`}
              fill="#374151"
            />
          )}
          <circle cx={s.x} cy="55" r="26" fill="#0d1117" stroke={s.color} strokeWidth="1.5" />
          <circle cx={s.x} cy="55" r="20" fill={`${s.color}18`} />
          <text x={s.x} y="51" textAnchor="middle" fill={s.color} fontSize="8" fontWeight="700">{i + 1}</text>
          <text x={s.x} y="98" textAnchor="middle" fill="#e2e8f0" fontSize="10" fontWeight="600">{s.label}</text>
          <text x={s.x} y="112" textAnchor="middle" fill="#64748b" fontSize="9">{s.sub}</text>
        </g>
      ))}
    </svg>
  )
}

// ── SVG: Alert Flow ──────────────────────────────────────────────────────────

function AlertFlowSVG() {
  const stages = [
    { label: 'Signal',        color: '#3b82f6', x: 30  },
    { label: 'Keyword Match', color: '#22c55e', x: 130 },
    { label: 'Dedup',         color: '#f59e0b', x: 255 },
    { label: 'Threshold',     color: '#ef4444', x: 375 },
    { label: 'Webhook/Push',  color: '#8b5cf6', x: 460 },
  ]
  const widths = [80, 105, 90, 80, 90]
  return (
    <svg viewBox="0 0 560 120" className="w-full" aria-label="Alert flow pipeline">
      {stages.map((s, i) => {
        const w = widths[i]
        return (
          <g key={s.label}>
            <rect x={s.x} y="38" width={w} height="44" rx="8"
              fill="#0d1117" stroke={s.color} strokeWidth="1.5" />
            <text x={s.x + w / 2} y="56" textAnchor="middle" fill={s.color} fontSize="9" fontWeight="700">
              {s.label.includes('/') ? s.label.split('/')[0] : s.label}
            </text>
            {s.label.includes('/') && (
              <text x={s.x + w / 2} y="68" textAnchor="middle" fill={s.color} fontSize="9" fontWeight="700">
                {s.label.split('/')[1]}
              </text>
            )}
            {i < stages.length - 1 && (
              <polygon
                points={`${s.x + w + 4},57 ${s.x + w + 14},60 ${s.x + w + 4},63`}
                fill="#374151"
              />
            )}
          </g>
        )
      })}
    </svg>
  )
}

// ── Constants ────────────────────────────────────────────────────────────────

const stats = [
  { value: '50M+',  label: 'Posts/day indexed' },
  { value: '4',     label: 'Platforms live' },
  { value: '<30s',  label: 'Alert latency' },
  { value: '95',    label: 'Languages supported' },
]

const useCases = [
  {
    icon: Shield,
    title: 'Threat Actor Tracking',
    desc: 'Follow known APT handles, alias clusters, and dark-web personas across every channel. Get alerted the moment they post, repost, or change narrative.',
    color: '#ef4444',
  },
  {
    icon: MessageSquare,
    title: 'Brand Protection',
    desc: 'Monitor brand mentions, detect impersonator accounts, and track coordinated inauthentic behavior targeting your organisation in real time.',
    color: '#3b82f6',
  },
  {
    icon: Radio,
    title: 'Disinformation Detection',
    desc: 'Cluster related posts into narrative threads, detect synchronized posting patterns, and surface influence operations before they go viral.',
    color: '#8b5cf6',
  },
]

const nlpCapabilities = [
  'Named-entity recognition — persons, orgs, locations, dates',
  'Sentiment scoring per post and per narrative cluster',
  'Language detection across 95 languages with auto-translation',
  'Coordinated activity fingerprinting (timing + content patterns)',
  'Toxicity and threat-level scoring',
  'Topic classification against 40+ OSINT categories',
]

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.55 } },
}

// ── Page ─────────────────────────────────────────────────────────────────────

export default function SocialStreamProduct() {
  return (
    <div className="min-h-screen bg-[#0a0f0a] text-white font-sans">
      <SEOHead
        title="Social Stream — Real-Time Multi-Platform Social Media Intelligence | Sycek OSINT"
        description="Monitor Twitter/X, Telegram, Reddit, and more in real time. AI-powered NLP for entity extraction, sentiment scoring, keyword alerts, hashtag trending, and influencer identification for threat monitoring and brand intelligence."
        keywords="social media monitoring, real-time social listening, Twitter monitoring, Telegram monitoring, Reddit monitoring, social media intelligence, SOCINT, keyword alerts, social media analytics, NLP social media, brand intelligence, threat monitoring, social media OSINT, hashtag tracking, influencer analysis"
        canonical="/social-stream-product"
        jsonLd={{
          "@type": "Product",
          "name": "Social Stream",
          "description": "Real-time multi-platform social media intelligence with AI-powered NLP, keyword alerts, and threat monitoring.",
          "brand": { "@type": "Brand", "name": "Sycek OSINT" },
          "url": "https://sycek.io/social-stream-product",
          "category": "Security Software"
        }}
      />
      <ProductPageNav activePage="Social Stream" />

      {/* ── Hero ── */}
      <section className="relative overflow-hidden py-28 px-6">
        {/* Background glow */}
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[400px] bg-green-500/[0.05] rounded-full blur-3xl" />
        </div>
        <div className="container mx-auto max-w-4xl text-center relative z-10">
          <motion.div initial="hidden" animate="visible" variants={fadeUp}>
            <Badge className="mb-6 bg-green-500/10 text-green-400 border-green-500/20 text-xs uppercase tracking-widest px-4 py-1.5">
              Social Intelligence
            </Badge>
            <h1 className="text-5xl md:text-6xl font-black leading-tight tracking-tight mb-6">
              Monitor Every Platform.{' '}
              <span className="text-green-400">Miss Nothing.</span>
            </h1>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto mb-10 leading-relaxed">
              Boolean keyword monitoring, real-time NLP enrichment, and sub-30-second
              alerts across Twitter/X, Telegram, Reddit, and Discord — unified in a
              single stream.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/register">
                <Button size="lg" className="bg-green-500 hover:bg-green-600 text-black font-bold px-8 shadow-lg shadow-green-500/30">
                  Start Monitoring
                </Button>
              </Link>
              <Link to="/app/social-stream">
                <Button size="lg" variant="outline" className="border-white/10 hover:border-green-500/40 hover:bg-green-500/[0.06] text-slate-300">
                  Live Demo
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── Stats ── */}
      <section className="border-y border-white/[0.06] py-14 px-6">
        <div className="container mx-auto max-w-4xl">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((s) => (
              <motion.div
                key={s.label}
                initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
                className="text-center"
              >
                <div className="text-4xl font-black text-green-400 mb-1">{s.value}</div>
                <div className="text-sm text-slate-500">{s.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Platform Feed SVG ── */}
      <section className="py-20 px-6">
        <div className="container mx-auto max-w-5xl">
          <motion.div
            initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold mb-3">Four Platforms. One Stream.</h2>
            <p className="text-slate-400 max-w-xl mx-auto text-sm">
              Ingest data from every major social channel into a unified engine — normalised,
              deduplicated, and ready for analysis.
            </p>
          </motion.div>
          <motion.div
            initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
            className="rounded-2xl border border-white/[0.07] bg-[#0d1117] p-6"
          >
            <PlatformFeedSVG />
          </motion.div>
        </div>
      </section>

      {/* ── Keyword Monitor SVG ── */}
      <section className="py-20 px-6 bg-[#0c110c]">
        <div className="container mx-auto max-w-5xl">
          <motion.div
            initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold mb-3">Boolean Query Builder</h2>
            <p className="text-slate-400 max-w-xl mx-auto text-sm">
              Compose AND / OR / NOT queries with regex support and per-language filters.
              See live match counts the moment you save.
            </p>
          </motion.div>
          <motion.div
            initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
            className="rounded-2xl border border-white/[0.07] bg-[#0d1117] p-6"
          >
            <KeywordMonitorSVG />
          </motion.div>
          <motion.div
            initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
            className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4"
          >
            {[
              { label: 'Boolean Logic', desc: 'AND / OR / NOT with nested groups' },
              { label: 'Regex Support', desc: 'Full PCRE pattern matching on post body' },
              { label: 'Language Filter', desc: 'Scope queries to specific language codes' },
            ].map((f) => (
              <div key={f.label} className="rounded-xl border border-white/[0.07] bg-[#0d1117] p-5">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                  <span className="text-sm font-semibold text-white">{f.label}</span>
                </div>
                <p className="text-xs text-slate-500">{f.desc}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── NLP Pipeline ── */}
      <section className="py-20 px-6">
        <div className="container mx-auto max-w-5xl">
          <motion.div
            initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold mb-3">NLP Enrichment Pipeline</h2>
            <p className="text-slate-400 max-w-xl mx-auto text-sm">
              Every post runs through a five-stage NLP pipeline before it reaches your feed.
            </p>
          </motion.div>
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <motion.div
              initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
              className="rounded-2xl border border-white/[0.07] bg-[#0d1117] p-6"
            >
              <NLPPipelineSVG />
            </motion.div>
            <motion.div
              initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
            >
              <h3 className="text-lg font-bold text-white mb-5">What the pipeline delivers</h3>
              <ul className="space-y-3">
                {nlpCapabilities.map((cap) => (
                  <li key={cap} className="flex items-start gap-3 text-sm text-slate-400">
                    <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                    {cap}
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── Alert Flow ── */}
      <section className="py-20 px-6 bg-[#0c110c]">
        <div className="container mx-auto max-w-5xl">
          <motion.div
            initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold mb-3">Sub-30-Second Alert Pipeline</h2>
            <p className="text-slate-400 max-w-xl mx-auto text-sm">
              From raw signal to webhook delivery in under 30 seconds — with dedup and
              threshold guards to eliminate noise.
            </p>
          </motion.div>
          <motion.div
            initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
            className="rounded-2xl border border-white/[0.07] bg-[#0d1117] p-6"
          >
            <AlertFlowSVG />
          </motion.div>
          <motion.div
            initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
            className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4"
          >
            {[
              { icon: Bell,          label: 'Webhook delivery',      desc: 'POST to any HTTPS endpoint within 30 s of a match' },
              { icon: Zap,           label: 'In-app push alerts',     desc: 'Real-time browser notifications for live sessions' },
              { icon: Hash,          label: 'Threshold controls',     desc: 'Set minimum match frequency to avoid alert fatigue' },
              { icon: AlertTriangle, label: 'Deduplication engine',   desc: 'Fingerprint-based dedup across a configurable window' },
            ].map((f) => (
              <div key={f.label} className="rounded-xl border border-white/[0.07] bg-[#0d1117] p-5 flex items-start gap-4">
                <f.icon className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                <div>
                  <div className="text-sm font-semibold text-white mb-1">{f.label}</div>
                  <div className="text-xs text-slate-500">{f.desc}</div>
                </div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── Use Cases ── */}
      <section className="py-20 px-6">
        <div className="container mx-auto max-w-5xl">
          <motion.div
            initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold mb-3">Who Uses Social Stream</h2>
            <p className="text-slate-400 max-w-xl mx-auto text-sm">
              From SOC teams tracking threat actors to comms teams protecting brand integrity.
            </p>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-6">
            {useCases.map((uc) => (
              <motion.div
                key={uc.title}
                initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
                className="rounded-2xl border border-white/[0.07] bg-[#0d1117] p-7"
              >
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center mb-4"
                  style={{ background: `${uc.color}18`, border: `1px solid ${uc.color}40` }}
                >
                  <uc.icon className="w-5 h-5" style={{ color: uc.color }} />
                </div>
                <h3 className="text-base font-bold text-white mb-2">{uc.title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{uc.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-24 px-6">
        <div className="container mx-auto max-w-3xl text-center">
          <motion.div
            initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
          >
            <div className="rounded-2xl border border-green-500/20 bg-green-500/[0.04] px-10 py-14">
              <h2 className="text-4xl font-black mb-4">Start Monitoring Now</h2>
              <p className="text-slate-400 mb-8 max-w-md mx-auto text-sm">
                Connect your first keyword monitor in under 2 minutes. No credit card required
                to start your trial.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/register">
                  <Button size="lg" className="bg-green-500 hover:bg-green-600 text-black font-bold px-10 shadow-lg shadow-green-500/30">
                    Start Monitoring Now
                  </Button>
                </Link>
                <Link to="/app/social-stream">
                  <Button size="lg" variant="outline" className="border-white/10 hover:border-green-500/40 text-slate-300">
                    Live Demo
                  </Button>
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <ProductPageFooter />
    </div>
  )
}
